import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Search, Filter, Grid, List, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Products = () => {
  const { t } = useTranslation();
  const { language, isRTL } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  const productCategories = [
    /*
    {
      key: 'building',
      image: 'https://images.pexels.com/photos/237950/pexels-photo-237950.jpeg',
      description: language === 'en' 
        ? 'High-quality construction materials including cement, concrete, steel, and aggregates for all your building needs.'
        : 'مواد بناء عالية الجودة تشمل الأسمنت والخرسانة والصلب والركام لجميع احتياجات البناء الخاصة بك.'
    },
    */
    {
      key: 'electrical',
      image: 'https://images.pexels.com/photos/32588549/pexels-photo-32588549.jpeg',
      description: language === 'en' 
        ? 'Complete range of Modern lighting solutions including LED lights, fixtures, industrial lighting, smart lighting systems, and electrical components for residential and commercial projects.'
        : 'مجموعة كاملة من حلول الإضاءة الحديثة تشمل أضواء LED، والتجهيزات، والإضاءة الصناعية، وأنظمة الإضاءة الذكية، والمكونات الكهربائية للمشاريع السكنية والتجارية.'
    },
    {
      key: 'fixtures',
      image: 'https://images.unsplash.com/photo-1714399913207-b9198ba711f9',
      description: language === 'en' 
        ? 'Premium fixtures and fittings including plumbing fixtures, hardware, handles, and architectural elements.'
        : 'تركيبات ومستلزمات متميزة تشمل تركيبات السباكة والأجهزة والمقابض والعناصر المعمارية.'
    },
    /*
    {
      key: 'lighting',
      image: 'https://images.unsplash.com/photo-1603545440473-5f2a1f8f014e',
      description: language === 'en' 
        ? 'Modern lighting solutions including LED lights, fixtures, industrial lighting, and smart lighting systems.'
        : 'حلول إضاءة حديثة تشمل أضواء LED والتركيبات والإضاءة الصناعية وأنظمة الإضاءة الذكية.'
    },
    */
    {
      key: 'stones',
      image: 'https://images.unsplash.com/photo-1584354273341-3eb96574e5be',
      description: language === 'en' 
        ? 'Exquisite natural stones and marble for flooring, walls, countertops, and decorative applications.'
        : 'أحجار طبيعية ورخام رائع للأرضيات والجدران وأسطح العمل والتطبيقات الزخرفية.'
    },
    /*
    {
      key: 'steel',
      image: 'https://images.pexels.com/photos/46167/iron-rods-reinforcing-bars-rods-steel-bars-46167.jpeg',
      description: language === 'en' 
        ? 'High-grade reinforcement steel bars and structural steel products meeting international construction standards.'
        : 'قضبان حديد التسليح عالية الجودة ومنتجات الصلب الإنشائي التي تلبي معايير البناء الدولية.'
    },
    */
    {
      key: 'sanitary',
      image: 'https://images.pexels.com/photos/6032203/pexels-photo-6032203.jpeg',
      description: language === 'en' 
        ? 'Complete range of sanitary ware including toilets, sinks, bathtubs, and bathroom accessories from leading brands.'
        : 'مجموعة كاملة من الأدوات الصحية تشمل المراحيض والأحواض وأحواض الاستحمام وإكسسوارات الحمام من العلامات التجارية الرائدة.'
    },
    {
      key: 'tiles',
      image: 'https://images.unsplash.com/photo-1580398562556-d33329a0f29b',
      description: language === 'en' 
        ? 'Diverse selection of tiles and flooring materials including ceramic, porcelain, natural stone, and luxury vinyl.'
        : 'مجموعة متنوعة من البلاط ومواد الأرضيات تشمل السيراميك والبورسلين والحجر الطبيعي والفينيل الفاخر.'
    }
  ];

  const filteredCategories = productCategories.filter(category => {
    const matchesSearch = t(`products.categories.${category.key}`).toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || category.key === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  useEffect(() => {
    // Handle hash navigation for direct category access
    const hash = window.location.hash.replace('#', '');
    if (hash && productCategories.find(cat => cat.key === hash)) {
      setSelectedCategory(hash);
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  return (
    <div className="min-h-screen pt-32 pb-20">
      {/* Header Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-purple-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-islamic-pattern opacity-10" />
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className={`max-w-4xl mx-auto ${isRTL ? 'font-arabic' : ''}`}
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {t('products.title')}
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              {t('products.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-8 bg-white shadow-lg sticky top-28 z-40">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className={`absolute top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
              <input
                type="text"
                placeholder={language === 'en' ? 'Search products...' : 'البحث عن المنتجات...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full ${isRTL ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <Filter className="w-5 h-5 text-gray-600" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className={`px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${isRTL ? 'text-right' : ''}`}
              >
                <option value="all">
                  {language === 'en' ? 'All Categories' : 'جميع الفئات'}
                </option>
                {productCategories.map((category) => (
                  <option key={category.key} value={category.key}>
                    {t(`products.categories.${category.key}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid/List */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          {filteredCategories.length === 0 ? (
            <div className={`text-center py-20 ${isRTL ? 'font-arabic' : ''}`}>
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold text-gray-700 mb-2">
                {language === 'en' ? 'No products found' : 'لم يتم العثور على منتجات'}
              </h3>
              <p className="text-gray-500">
                {language === 'en' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'جرب تعديل معايير البحث أو التصفية'
                }
              </p>
            </div>
          ) : (
            <div className={`${
              viewMode === 'grid' 
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8' 
                : 'space-y-8'
            }`}>
              {filteredCategories.map((category, index) => (
                <motion.div
                  key={category.key}
                  id={category.key}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className={`group ${
                    viewMode === 'list' ? 'flex flex-col md:flex-row gap-6' : ''
                  }`}
                >
                  <div className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 ${
                    viewMode === 'list' ? 'flex-1' : ''
                  }`}>
                    <div className={`relative overflow-hidden ${
                      viewMode === 'list' ? 'md:w-1/3 h-64 md:h-auto flex-shrink-0' : 'h-64'
                    }`}>
                      <img
                        src={category.image}
                        alt={t(`products.categories.${category.key}`)}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent" />
                    </div>
                    
                    <div className={`p-6 ${viewMode === 'list' ? 'flex-1 flex flex-col justify-between' : ''}`}>
                      <div>
                        <h3 className={`text-2xl font-bold text-gray-900 mb-4 ${isRTL ? 'font-arabic text-right' : ''}`}>
                          {t(`products.categories.${category.key}`)}
                        </h3>
                        <p className={`text-gray-600 mb-6 leading-relaxed ${isRTL ? 'font-arabic text-right' : ''}`}>
                          {category.description}
                        </p>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Link
                          to="/quote"
                          className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-full font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center space-x-2 rtl:space-x-reverse"
                        >
                          <span>{t('products.requestQuote')}</span>
                          <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                        </Link>
                        <Link
                          to="/contact"
                          className="border-2 border-blue-600 text-blue-600 px-6 py-3 rounded-full font-semibold hover:bg-blue-600 hover:text-white transition-all duration-300 text-center"
                        >
                          {language === 'en' ? 'Learn More' : 'اعرف المزيد'}
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-purple-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-islamic-pattern opacity-10" />
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={`max-w-4xl mx-auto ${isRTL ? 'font-arabic' : ''}`}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {language === 'en' 
                ? 'Need a Custom Solution?'
                : 'تحتاج إلى حل مخصص؟'
              }
            </h2>
            <p className="text-xl mb-10 text-gray-200">
              {language === 'en' 
                ? 'Our experts can help you find the perfect materials for your specific project requirements.'
                : 'يمكن لخبرائنا مساعدتك في العثور على المواد المثالية لمتطلبات مشروعك المحددة.'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/quote"
                className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:from-yellow-600 hover:to-orange-600 transition-all duration-300 shadow-xl hover:shadow-yellow-500/25 hover:scale-105 inline-flex items-center justify-center space-x-2 rtl:space-x-reverse"
              >
                <span>{t('nav.quote')}</span>
                <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-blue-900 transition-all duration-300 shadow-xl hover:scale-105"
              >
                {t('nav.contact')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Products;